import React from 'react';
import LegalDocumentLayout, {
  LegalSection,
  LegalList,
  LegalListItem
} from '../components/legal/LegalDocumentLayout';

const TermsOfServicePage = () => (
  <LegalDocumentLayout
    title="Terms of Service"
    subtitle="The agreement between you and Niko Vibe when you browse events, buy tickets, or use our platform."
  >
    <p className="text-gray-700 text-base md:text-[1.05rem] leading-relaxed">
      These Terms of Service (&quot;Terms&quot;) govern your access to and use of the Niko Vibe website,
      mobile experiences, and related services (collectively, the &quot;Platform&quot;) operated by Niko Vibe
      (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing the Platform or purchasing tickets, you agree to these Terms.
      If you do not agree, do not use the Platform.
    </p>

    <LegalSection title="1. Our role">
      <p>
        Niko Vibe provides an online ticketing and event discovery platform. We act as an intermediary
        between event organizers (&quot;Organizers&quot;) and ticket buyers (&quot;you&quot; or &quot;Attendees&quot;). Unless
        explicitly stated, Niko Vibe is not the organizer, producer, or owner of events listed on the Platform.
      </p>
      <p>
        Organizers are solely responsible for event production, scheduling, venue access, safety, content,
        and fulfillment of the event experience. Any claims relating to the event itself should be directed
        to the Organizer, subject to applicable law and the Organizer&apos;s stated policies.
      </p>
    </LegalSection>

    <LegalSection title="2. Eligibility and accounts">
      <p>
        You must be at least 18 years old, or the age of majority in your jurisdiction, to create an account
        or complete a purchase. If you purchase on behalf of a minor, you represent that you are their parent
        or legal guardian and accept these Terms on their behalf.
      </p>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for all activity
        under your account. Notify us immediately of any unauthorized use at info@nikovibe.com.
      </p>
    </LegalSection>

    <LegalSection title="3. Ticket purchases">
      <p>
        When you purchase a ticket, you enter into a contract with the Organizer for attendance at the event.
        Niko Vibe facilitates payment collection and ticket delivery on the Organizer&apos;s behalf.
      </p>
      <LegalList>
        <LegalListItem>All prices are displayed in Kenyan Shillings (KES) unless otherwise stated.</LegalListItem>
        <LegalListItem>
          Prices, packages, and availability are set by Organizers and may change until checkout is completed.
        </LegalListItem>
        <LegalListItem>
          A completed payment constitutes a confirmed order subject to inventory and fraud review.
        </LegalListItem>
        <LegalListItem>
          Tickets are generally non-transferable unless the Organizer or Platform expressly allows transfer.
        </LegalListItem>
        <LegalListItem>
          You must provide accurate contact information so tickets and event updates can be delivered.
        </LegalListItem>
      </LegalList>
    </LegalSection>

    <LegalSection title="4. Refunds, cancellations, and rescheduled events">
      <p>
        Refund eligibility is governed by the refund policy displayed on each event page at the time of purchase
        and by the Organizer&apos;s policies. Niko Vibe does not guarantee refunds except where required by law
        or where we explicitly state otherwise.
      </p>
      <p>
        If an event is cancelled, rescheduled, or materially changed, the Organizer is responsible for communicating
        options to Attendees. Where the Organizer authorizes refunds, Niko Vibe will process approved refunds
        through the original payment method where reasonably possible. Processing times may depend on banks and
        mobile money providers such as M-Pesa.
      </p>
      <p>
        Service fees, payment processing costs, or third-party charges may be non-refundable unless required by law
        or expressly included in an approved refund.
      </p>
    </LegalSection>

    <LegalSection title="5. Event entry and conduct">
      <p>
        Admission to an event is subject to the Organizer&apos;s rules, venue policies, age restrictions, and
        valid presentation of your ticket (including QR code or other verification). You may be refused entry
        for safety, legal, or policy reasons without entitlement to a refund unless required by law.
      </p>
      <p>
        You agree not to engage in unlawful, abusive, fraudulent, or disruptive conduct on the Platform or at
        events, including scalping tickets in violation of Organizer rules, using bots to purchase inventory,
        or attempting to circumvent security or access controls.
      </p>
    </LegalSection>

    <LegalSection title="6. Intellectual property">
      <p>
        The Platform, branding, software, and content we provide are owned by Niko Vibe or our licensors.
        Event names, images, and descriptions are owned by Organizers or their licensors. You may not copy,
        scrape, reverse engineer, or commercially exploit Platform content without written permission.
      </p>
    </LegalSection>

    <LegalSection title="7. Disclaimers">
      <p>
        THE PLATFORM AND TICKETING SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; TO THE FULLEST EXTENT
        PERMITTED BY LAW. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS
        FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>
      <p>
        We do not warrant uninterrupted or error-free operation, that defects will be corrected, or that the
        Platform is free of viruses or harmful components. Event details are supplied by Organizers; we do not
        guarantee their accuracy or completeness.
      </p>
    </LegalSection>

    <LegalSection title="8. Limitation of liability">
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NIKO VIBE AND ITS DIRECTORS, EMPLOYEES, AGENTS,
        AND PARTNERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR FOR LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM YOUR USE OF
        THE PLATFORM OR ATTENDANCE AT AN EVENT.
      </p>
      <p>
        OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM SHALL NOT
        EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO NIKO VIBE FOR THE TICKET(S) GIVING RISE TO THE
        CLAIM IN THE TWELVE (12) MONTHS BEFORE THE CLAIM, OR (B) KES 10,000.
      </p>
      <p>
        Nothing in these Terms excludes liability that cannot be excluded under Kenyan law, including liability
        for fraud or wilful misconduct.
      </p>
    </LegalSection>

    <LegalSection title="9. Indemnity">
      <p>
        You agree to indemnify and hold harmless Niko Vibe from claims, losses, and expenses (including
        reasonable legal fees) arising from your breach of these Terms, misuse of the Platform, or violation
        of any law or third-party rights.
      </p>
    </LegalSection>

    <LegalSection title="10. Governing law and disputes">
      <p>
        These Terms are governed by the laws of the Republic of Kenya. You agree to submit to the exclusive
        jurisdiction of the courts of Nairobi, Kenya, subject to any mandatory consumer protection provisions
        that apply in your jurisdiction.
      </p>
      <p>
        Before initiating formal proceedings, you agree to contact us at info@nikovibe.com to attempt to
        resolve the dispute in good faith within thirty (30) days.
      </p>
    </LegalSection>

    <LegalSection title="11. Changes to these Terms">
      <p>
        We may update these Terms from time to time. Material changes will be posted on this page with an
        updated &quot;Last updated&quot; date. Continued use of the Platform after changes constitutes acceptance of
        the revised Terms.
      </p>
    </LegalSection>
  </LegalDocumentLayout>
);

export default TermsOfServicePage;
